import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber, DatePicker, Select } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import {
  getCouponsApi,
  createCouponApi,
  updateCouponApi,
  deleteCouponApi,
  getProductsApi,
  getProductByIdApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";
import AppSelect from "../../common/FormInputs/AppSelect";

const ACCENT = "#534ab7";

const DISCOUNT_TYPES = [
  { value: "PERCENTAGE", label: "Percentage (%)" },
  { value: "FLAT",       label: "Flat amount (₹)" },
];

export default function Coupons() {
  const [coupons,  setCoupons]  = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [discountType, setDiscountType] = useState("PERCENTAGE");
  const [form] = Form.useForm();
  // Product picker for product-scoped free delivery. Options are {value, label}
  // pairs fetched on demand (the catalogue is too big to preload).
  const [productOptions, setProductOptions] = useState([]);
  const [productSearching, setProductSearching] = useState(false);

  const productLabel = (p) =>
    `${p.name || p.title || `Product #${p.id}`}${p.created_by ? ` · seller #${p.created_by}` : ""}`;

  const searchProducts = async (q) => {
    setProductSearching(true);
    try {
      const res = await getProductsApi({ page: 0, size: 20, search: q || undefined });
      const items = res.data?.data?.items || [];
      setProductOptions((prev) => {
        // keep already-selected options so their labels don't vanish mid-search
        const selected = new Set(form.getFieldValue("freeDeliveryProductIds") || []);
        const kept = prev.filter((o) => selected.has(o.value));
        const fresh = items.map((p) => ({ value: p.id, label: productLabel(p) }));
        return [...kept, ...fresh.filter((o) => !kept.some((k) => k.value === o.value))];
      });
    } catch {
      /* picker just stays empty */
    } finally {
      setProductSearching(false);
    }
  };

  const loadSelectedProducts = async (ids) => {
    const opts = await Promise.all(
      ids.map((id) =>
        getProductByIdApi(id)
          .then((r) => ({ value: id, label: productLabel(r.data?.data || { id }) }))
          .catch(() => ({ value: id, label: `Product #${id}` }))
      )
    );
    setProductOptions(opts);
  };

  const fetchCoupons = async () => {
    try {
      const res = await getCouponsApi();
      setCoupons((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load coupons");
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const displayed = coupons.filter(
    (c) => !search || c.code?.toLowerCase().includes(search.toLowerCase())
  );

  const isExpired = (c) => c.expiryDate && dayjs(c.expiryDate).isBefore(dayjs(), "day");

  const openAdd = () => {
    setEditing(null);
    setDiscountType("PERCENTAGE");
    form.resetFields();
    form.setFieldsValue({
      discountType: "PERCENTAGE",
      isActive: true,
      minOrderAmount: 0,
      freeDelivery: false,
      freeDeliveryProductIds: [],
    });
    setProductOptions([]);
    searchProducts("");
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setDiscountType(record.discountType || "PERCENTAGE");
    form.setFieldsValue({
      code:             record.code,
      discountType:     record.discountType,
      discountValue:    record.discountValue,
      minOrderAmount:   record.minOrderAmount,
      maxDiscountAmount:record.maxDiscountAmount,
      expiryDate:       record.expiryDate ? dayjs(record.expiryDate) : null,
      freeDelivery:     !!record.freeDelivery,
      freeDeliveryProductIds: record.freeDeliveryProductIds || [],
      isActive:         record.active,
    });
    loadSelectedProducts(record.freeDeliveryProductIds || []).then(() => searchProducts(""));
    setOpen(true);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        code:              values.code,
        discountType:      values.discountType,
        discountValue:     values.discountValue ?? 0,
        freeDelivery:      !!values.freeDelivery,
        freeDeliveryProductIds: values.freeDelivery ? (values.freeDeliveryProductIds || []) : [],
        minOrderAmount:    values.minOrderAmount ?? 0,
        maxDiscountAmount: values.discountType === "PERCENTAGE" ? (values.maxDiscountAmount ?? null) : null,
        expiryDate:        values.expiryDate ? values.expiryDate.format("YYYY-MM-DD") : null,
        active:            values.isActive,
      };
      if (editing) {
        await updateCouponApi(editing.id, payload);
        message.success("Coupon updated");
      } else {
        await createCouponApi(payload);
        message.success("Coupon added");
      }
      setOpen(false);
      fetchCoupons();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save coupon");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateCouponApi(record.id, {
        code:              record.code,
        discountType:      record.discountType,
        discountValue:     record.discountValue,
        minOrderAmount:    record.minOrderAmount,
        maxDiscountAmount: record.maxDiscountAmount,
        freeDelivery:      !!record.freeDelivery,
        freeDeliveryProductIds: record.freeDeliveryProductIds || [],
        expiryDate:        record.expiryDate,
        active:            !record.active,
      });
      message.success(record.active ? "Coupon deactivated" : "Coupon activated");
      fetchCoupons();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this coupon?",
      content: "This cannot be undone. Customers will no longer be able to apply this code.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteCouponApi(id);
          message.success("Coupon deleted");
          fetchCoupons();
        } catch {
          message.error("Failed to delete coupon");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Code",
      dataIndex: "code",
      render: (v) => <span style={{ fontWeight: 700, color: "#111827", letterSpacing: 0.5 }}>{v}</span>,
    },
    {
      title: "Discount",
      render: (_, r) => (
        <span>
          {r.discountValue > 0
            ? (r.discountType === "PERCENTAGE" ? `${r.discountValue}%` : `₹${r.discountValue}`)
            : (r.freeDelivery ? null : "—")}
          {r.freeDelivery ? (
            <Tag color="blue" style={{ marginLeft: r.discountValue > 0 ? 6 : 0 }}>
              {r.freeDeliveryProductIds?.length
                ? `Free delivery · ${r.freeDeliveryProductIds.length} product${r.freeDeliveryProductIds.length > 1 ? "s" : ""}`
                : "Free delivery · all products"}
            </Tag>
          ) : null}
          {r.discountType === "PERCENTAGE" && r.maxDiscountAmount
            ? <span style={{ color: "#9CA3AF", fontSize: 12 }}> (max ₹{r.maxDiscountAmount})</span>
            : null}
        </span>
      ),
    },
    {
      title: "Min Order",
      dataIndex: "minOrderAmount",
      render: (v) => `₹${v || 0}`,
    },
    {
      title: "Expiry",
      dataIndex: "expiryDate",
      render: (v, r) => v
        ? <Tag color={isExpired(r) ? "red" : "default"}>{dayjs(v).format("DD MMM YYYY")}</Tag>
        : <span style={{ color: "#ccc" }}>No expiry</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 110,
      align: "center",
      render: (v, r) => isExpired(r)
        ? <Tag color="red">Expired</Tag>
        : v ? <Tag color="green">Active</Tag> : <Tag color="default">Off</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Coupons</div>
          <div className="page-sub">{coupons.length} coupons — only active, unexpired coupons are valid at checkout</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Coupon
        </Button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search coupon codes…"
          allowClear
          style={{ width: 280, borderRadius: 8 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AppTable columns={columns} data={displayed} />

      <Modal
        title={editing ? `Edit — ${editing.code}` : "Add Coupon"}
        open={open}
        okText={editing ? "Save Changes" : "Add Coupon"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <AppInput
            name="code"
            label="Coupon Code"
            rules={[{ required: true, message: "Code is required" }]}
            placeholder="e.g. WELCOME10"
            style={{ textTransform: "uppercase" }}
          />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppSelect
                name="discountType"
                label="Discount Type"
                rules={[{ required: true, message: "Pick a discount type" }]}
                options={DISCOUNT_TYPES}
                onChange={(v) => setDiscountType(v)}
              />
            </div>
            <Form.Item
              name="discountValue"
              label={discountType === "PERCENTAGE" ? "Percentage" : "Amount (₹)"}
              rules={[({ getFieldValue }) => ({
                validator: (_, v) =>
                  (v && v > 0) || getFieldValue("freeDelivery")
                    ? Promise.resolve()
                    : Promise.reject(new Error("Required (or turn on Free delivery)")),
              })]}
              dependencies={["freeDelivery"]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={discountType === "PERCENTAGE" ? 100 : undefined} style={{ width: "100%" }} />
            </Form.Item>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="minOrderAmount" label="Min Order Amount (₹)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>
            {discountType === "PERCENTAGE" && (
              <Form.Item name="maxDiscountAmount" label="Max Discount Cap (₹)" style={{ flex: 1 }}>
                <InputNumber min={0} style={{ width: "100%" }} placeholder="No cap" />
              </Form.Item>
            )}
          </div>

          <Form.Item
            name="freeDelivery"
            label="Free delivery"
            valuePropName="checked"
            extra="Waives the delivery charge on the order when this coupon is applied. Leave the discount at 0 for a free-delivery-only coupon."
          >
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>

          <Form.Item noStyle shouldUpdate={(a, b) => a.freeDelivery !== b.freeDelivery}>
            {({ getFieldValue }) =>
              getFieldValue("freeDelivery") ? (
                <Form.Item
                  name="freeDeliveryProductIds"
                  label="Free delivery only for these products"
                  extra="Leave empty to waive delivery on the whole cart. If you pick products, only those ship free — any other item in the cart still pays delivery."
                >
                  <Select
                    mode="multiple"
                    showSearch
                    filterOption={false}
                    onSearch={searchProducts}
                    loading={productSearching}
                    options={productOptions}
                    placeholder="Search products…"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <Form.Item name="expiryDate" label="Expiry Date">
            <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" disabledDate={(d) => d && d.isBefore(dayjs(), "day")} />
          </Form.Item>

          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
